import { useMemo } from "react"

import { useRoomPriceQuery } from "shared/api/price/queries.generated"

import { useRoomQuery } from "entities/rooms/graphql/queries.generated"
import { useProjectStore } from "features/project-management/model/useProjectStore"

import { AggregatedRoomData } from "../types/AggregatedRoom"

export interface AggregatedRoomDataOptions {
  includePrice?: boolean
}

export function useAggregatedRoomData({
  roomID,
  projectID,
  options
}: {
  roomID: string
  projectID: string
  options?: AggregatedRoomDataOptions
}): AggregatedRoomData {
  const { pendingElementIDs } = useProjectStore(projectID)
  const isPending = pendingElementIDs.includes(roomID)

  const {
    data: roomData,
    loading: isLoadingRoom,
    error: roomError
  } = useRoomQuery({ variables: { id: roomID }, skip: isPending })

  const {
    data: priceData,
    loading: isLoadingPrice,
    error: priceError
  } = useRoomPriceQuery({
    variables: { id: roomID },
    skip: !options?.includePrice || isPending
  })

  const roomPrice = priceData?.roomPrice ?? null
  const price = useMemo(
    () =>
      options?.includePrice
        ? {
            data: roomPrice,
            isLoading: isLoadingPrice || (!priceError && !roomPrice?.isValid),
            error: !!priceError
          }
        : null,
    [options?.includePrice, roomPrice, isLoadingPrice, priceError]
  )

  return {
    room: {
      data: roomData?.room ?? null,
      isLoading: isLoadingRoom || isPending,
      error: !!roomError
    },
    price,
    meta: { isPending }
  }
}
